import { ChevronRight, MapPin } from 'lucide-react'
import { useStoragePlaces } from '@/hooks/useStoragePlaces'
import { fullLocation, PLACE_SEPARATOR, placeOfPath } from '@/lib/facilityLocation'
import { formatLocation } from '@/lib/storageTree'
import { cn } from '@/lib/utils'

/**
 * A storage location for a table cell: where its unit stands — Facility ›
 * Building › Floor › Room — on top, the position beneath. The whole address,
 * in two lines a column can hold; hover shows it as one.
 */
export function LocationLines({ path, className }: { path: string; className?: string }) {
  const places = useStoragePlaces()
  const place = placeOfPath(path, places)
  return (
    <span className={cn('block min-w-0', className)} title={fullLocation(path, places)}>
      <span className={cn('block truncate text-xs', place ? 'text-muted-foreground' : 'italic text-muted-foreground/70')}>
        {place ?? 'Unit not placed in a room'}
      </span>
      <span className='block truncate text-sm font-medium text-foreground'>{formatLocation(path)}</span>
    </span>
  )
}

/**
 * A storage location as its full address — Facility › Building › Floor › Room
 * › unit › … › slot — one step per segment, the slot itself emphasised.
 *
 * The same text `useLocationText` writes everywhere else, so a location reads
 * the same here as in every table; this only lays it out as a trail. A unit
 * that stands in no room has no address above it, and the trail says so
 * rather than looking like a full address missing its start.
 */
export function LocationTrail({ path, className }: { path: string; className?: string }) {
  const places = useStoragePlaces()
  const text = fullLocation(path, places)
  const segments = text.split(PLACE_SEPARATOR)
  const placed = placeOfPath(path, places) != null
  return (
    <div className={cn('text-sm', className)} title={text}>
      <p className='flex flex-wrap items-center gap-x-1 gap-y-0.5'>
        <MapPin className='mr-0.5 h-3.5 w-3.5 shrink-0 text-primary' />
        {segments.map((segment, i) => (
          <span key={i} className='flex items-center gap-1'>
            {i > 0 && <ChevronRight className='h-3 w-3 shrink-0 text-muted-foreground/60' />}
            <span className={i === segments.length - 1 ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
              {segment}
            </span>
          </span>
        ))}
      </p>
      {!placed && (
        <p className='mt-1 text-xs text-muted-foreground'>
          This storage unit is not placed in a room yet, so its facility, building and floor are not known.
        </p>
      )}
    </div>
  )
}
